import { useEffect } from 'react';

type SeoProps = {
  title: string;
  description: string;
  path: string;
  image?: string;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
};

const SITE_NAME = 'PrivateFileKit';
const SITE_URL = 'https://privatefilekit.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/facebook-share-preview.png`;

function upsertMeta(selector: string, attributes: Record<string, string>, content: string) {
  let meta = document.head.querySelector<HTMLMetaElement>(selector);
  if (!meta) {
    meta = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => meta?.setAttribute(key, value));
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

export function Seo({ title, description, path, image, structuredData }: SeoProps) {
  useEffect(() => {
    const canonicalUrl = new URL(path, `${SITE_URL}/`).toString();
    const socialImage = image || DEFAULT_OG_IMAGE;
    document.title = title;

    upsertMeta('meta[name="description"]', { name: 'description' }, description);
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, title);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, description);
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, 'website');
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, SITE_NAME);
    upsertMeta('meta[property="og:image"]', { property: 'og:image' }, socialImage);
    upsertMeta('meta[property="og:image:url"]', { property: 'og:image:url' }, socialImage);
    upsertMeta('meta[property="og:image:secure_url"]', { property: 'og:image:secure_url' }, socialImage);
    upsertMeta('meta[property="og:image:type"]', { property: 'og:image:type' }, 'image/png');
    upsertMeta('meta[property="og:image:width"]', { property: 'og:image:width' }, '1200');
    upsertMeta('meta[property="og:image:height"]', { property: 'og:image:height' }, '630');
    upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt' }, 'PrivateFileKit branded social preview card');
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale' }, 'en_US');
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, socialImage);
    upsertMeta('meta[name="twitter:image:src"]', { name: 'twitter:image:src' }, socialImage);
    upsertMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt' }, 'PrivateFileKit branded social preview card');

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    let script = document.head.querySelector<HTMLScriptElement>('script[data-seo-structured-data="true"]');
    if (structuredData) {
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.dataset.seoStructuredData = 'true';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    } else if (script) {
      script.remove();
    }

    return () => {
      const current = document.head.querySelector<HTMLScriptElement>('script[data-seo-structured-data="true"]');
      if (current && !structuredData) {
        current.remove();
      }
    };
  }, [description, image, path, structuredData, title]);

  return null;
}
